import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle, XCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ConfirmCtx = createContext(null);

const VARIANTS = {
  danger:  { Icon: AlertTriangle, accent: 'from-red-500 to-red-600',    text: 'text-red-600',    border: 'border-red-200',    bg: 'bg-red-50',    btn: 'bg-red-600 hover:bg-red-700 text-white' },
  warn:    { Icon: AlertTriangle, accent: 'from-amber-500 to-amber-600', text: 'text-amber-600', border: 'border-amber-200', bg: 'bg-amber-50', btn: 'bg-amber-600 hover:bg-amber-700 text-white' },
  destroy: { Icon: Trash2,        accent: 'from-red-500 to-rose-600',    text: 'text-red-600',    border: 'border-red-200',    bg: 'bg-red-50',    btn: 'bg-red-600 hover:bg-red-700 text-white' },
  info:    { Icon: Info,          accent: 'from-blue-500 to-blue-600',   text: 'text-blue-600',   border: 'border-blue-200',   bg: 'bg-blue-50',   btn: 'bg-blue-600 hover:bg-blue-700 text-white' },
  default: { Icon: HelpCircle,    accent: 'from-gray-600 to-gray-700',   text: 'text-gray-700',   border: 'border-gray-200',   bg: 'bg-gray-50',   btn: 'bg-red-600 hover:bg-red-700 text-white' },
};

/**
 * Provider that renders a single on-demand confirm dialog and exposes
 * a Promise-based `confirm()` via `useConfirm()`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, variant, confirmText });
 *   if (!ok) return;
 */
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts?.title || 'Are you sure?',
        message: opts?.message || '',
        variant: opts?.variant || 'default',
        confirmText: opts?.confirmText || 'Continue',
        cancelText: opts?.cancelText || 'Cancel',
      });
    });
  }, []);

  const close = useCallback((value) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState(null);
  }, []);

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4
                     bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
          role="dialog"
          aria-modal="true"
          onClick={() => close(false)}
          data-testid="confirm-dialog-backdrop"
        >
          <Card
            onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full p-0 overflow-hidden shadow-2xl animate-in zoom-in-95 duration-150"
            data-testid="confirm-dialog"
          >
            <ConfirmContent state={state} onDone={close} />
          </Card>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
};

const ConfirmContent = ({ state, onDone }) => {
  const v = VARIANTS[state.variant] || VARIANTS.default;
  const { Icon } = v;
  return (
    <>
      <div className={`h-1.5 bg-gradient-to-r ${v.accent}`} />
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className={`p-3 rounded-xl ${v.bg} ${v.border} border flex-shrink-0`}>
            <Icon className={`h-6 w-6 ${v.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 leading-tight">
              {state.title}
            </h2>
            {state.message && (
              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed whitespace-pre-line">
                {state.message}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDone(false)}
            className="text-gray-400 hover:text-gray-700 p-1 -mr-1 -mt-1"
            aria-label="Close"
            data-testid="confirm-close"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button
            variant="outline"
            onClick={() => onDone(false)}
            data-testid="confirm-cancel"
          >
            {state.cancelText}
          </Button>
          <Button
            className={v.btn}
            onClick={() => onDone(true)}
            data-testid="confirm-ok"
            autoFocus
          >
            {state.confirmText}
          </Button>
        </div>
      </div>
    </>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Graceful fallback to native if provider is missing so nothing crashes.
    return (opts) =>
      Promise.resolve(
        window.confirm(
          (opts?.title ? `${opts.title}\n\n` : '') + (opts?.message || ''),
        ),
      );
  }
  return ctx;
};

export default ConfirmProvider;
